import { FC } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { Entity } from "@common/types/entity";
import { useDeleteEntityMutation } from "@api/entity.api";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { HStack } from "@design-system/HStack/HStack";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper } from "@helpers/form";

interface Props {
    entity: Pick<Entity, "id" | "name" | "workspaceId">;
    onClose: () => void;
    onDeleted?: () => void;
}

interface DeleteEntityFormValues {
    entityName: string;
    hasConfirmedDeletion: boolean;
}

const fields = createFormikFields<DeleteEntityFormValues>();

export const DeleteEntityModal: FC<Props> = ({
    entity,
    onClose,
    onDeleted,
}) => {
    const deletion = useDeleteEntityMutation(entity);
    const { error, toast } = useToaster();
    const formik = useFormik<DeleteEntityFormValues>({
        initialValues: {
            entityName: "",
            hasConfirmedDeletion: false,
        },
        validate: (values) => {
            const errors: Partial<
                Record<keyof DeleteEntityFormValues, string>
            > = {};
            if (values.entityName !== entity.name) {
                errors.entityName = "Entity name is incorrect";
            }
            if (!values.hasConfirmedDeletion) {
                errors.hasConfirmedDeletion = "You must confirm to proceed";
            }
            return errors;
        },
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: deletion.isPending,
            handler: async () => {
                await deletion.mutateAsync();
                toast(`Deleted entity "${entity.name}"`);
                onClose();
                onDeleted?.();
            },
            failureHandler: async (submitError) => {
                error(submitError.message || "Failed to delete entity");
            },
        }),
    });

    return (
        <>
            <StandardModalHeader>Delete {entity.name}?</StandardModalHeader>
            <StandardModalBody>
                <FormikProvider value={formik}>
                    <Form>
                        <VStack gap={16}>
                            <section>
                                <p>
                                    Deleting this entity will permanently
                                    remove:
                                </p>
                                <ul>
                                    <li>
                                        Connected financial accounts and
                                        transactions
                                    </li>
                                    <li>Uploaded documents</li>
                                    <li>
                                        Accounting records, invoices, bills, and
                                        reports
                                    </li>
                                </ul>
                                <p>
                                    Deletion stops if another entity depends on
                                    these records. To confirm, type the entity
                                    name below.
                                </p>
                            </section>

                            <VStack gap={16}>
                                <fields.Text
                                    name="entityName"
                                    label="Entity name"
                                    size="xl"
                                />
                                <fields.Checkbox
                                    name="hasConfirmedDeletion"
                                    label="I understand this will remove the entity and all of its historical data."
                                />
                            </VStack>
                            <p className="text-danger">
                                Warning: This action cannot be undone.
                            </p>
                            <HStack gap={12} justify="end">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={onClose}
                                    disabled={deletion.isPending}
                                    size="xl"
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoader
                                    type="submit"
                                    variant="destructive"
                                    disabled={
                                        !formik.isValid || deletion.isPending
                                    }
                                    loading={deletion.isPending}
                                    size="xl"
                                >
                                    Delete entity
                                </ButtonWithLoader>
                            </HStack>
                        </VStack>
                    </Form>
                </FormikProvider>
            </StandardModalBody>
        </>
    );
};
